import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader,
} from "@/components/ui/sheet";
import {
  LayoutDashboard,
  Users,
  Car,
  Wallet,
  ScanLine,
  LogOut,
  Trophy,
  CalendarDays,
  ShieldCheck,
  Wrench,
  BookOpen,
  Menu,
  Settings,
  HardHat,
  Ban,
  Trash2 as TrashIcon,
  ClipboardList,
  BookMarked,
  ListTodo,
  BarChart3,
} from "lucide-react";
import { useNavPrefs } from "@/lib/navPrefs";

export const ALL_NAV_LINKS = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true, testId: "nav-dashboard", roles: ["admin", "commercial"] },
  { to: "/plaques", label: "Recherche", icon: ScanLine, testId: "nav-plaques", roles: ["admin", "commercial", "atelier"] },
  { to: "/calendrier", label: "Calendrier", icon: CalendarDays, testId: "nav-calendrier", roles: ["admin", "commercial", "atelier"] },
  { to: "/atelier", label: "Atelier", icon: Wrench, testId: "nav-atelier", roles: ["admin", "commercial", "atelier"] },
  { to: "/vo-atelier", label: "VO Atelier", icon: HardHat, testId: "nav-voatelier", roles: ["admin", "commercial", "atelier"] },
  { to: "/commandes", label: "Commandes Client", icon: ClipboardList, testId: "nav-commandes", roles: ["admin", "commercial"] },
  { to: "/taches", label: "Tâches internes", icon: ListTodo, testId: "nav-taches", roles: ["admin", "commercial"] },
  { to: "/ventes", label: "Ventes en cours", icon: Car, testId: "nav-ventes", roles: ["admin", "commercial"] },
  { to: "/paiements", label: "Paiements", icon: Wallet, testId: "nav-paiements", roles: ["admin", "commercial", "atelier"] },
  { to: "/vo-vendus", label: "VO vendus", icon: Trophy, testId: "nav-vovendus", roles: ["admin", "commercial"] },
  { to: "/bilan", label: "Bilan ventes", icon: BarChart3, testId: "nav-bilan", roles: ["admin"] },
  { to: "/clients", label: "Clients", icon: Users, testId: "nav-clients", roles: ["admin", "commercial"] },
  { to: "/adresses", label: "Adresses", icon: BookOpen, testId: "nav-adresses", roles: ["admin", "commercial", "atelier"] },
  { to: "/ventes-annulees", label: "Ventes annulées", icon: Ban, testId: "nav-vannulees", roles: ["admin", "commercial"] },
  { to: "/corbeille", label: "Corbeille", icon: TrashIcon, testId: "nav-corbeille", roles: ["admin", "commercial"] },
  { to: "/utilisateurs", label: "Utilisateurs", icon: ShieldCheck, testId: "nav-users", roles: ["admin"] },
  { to: "/parametres", label: "Paramètres", icon: Settings, testId: "nav-parametres", roles: ["admin", "commercial", "atelier"] },
  { to: "/aide-commercial", label: "Guide commercial", icon: BookMarked, testId: "nav-aide-commercial", roles: ["admin", "commercial"] },
];

function CurrentPageTitle({ links }) {
  const location = useLocation();
  const exact = links.find((l) => l.to === location.pathname);
  if (exact) return exact.label;
  const prefix = links
    .filter((l) => l.to !== "/" && location.pathname.startsWith(l.to))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return prefix?.label || "Gestion Concession Automobile";
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const role = user?.role || "commercial";
  const userId = user?.id || user?.email || "anon";

  // Filter by role first, then apply per-user nav prefs (order + hidden)
  const roleLinks = ALL_NAV_LINKS.filter((l) => l.roles.includes(role));
  const { applied } = useNavPrefs(userId, roleLinks);
  const links = applied.visible;

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/login");
  };

  const NavItems = ({ onNavigate }) => (
    <nav className="px-3 py-4 space-y-1 overflow-y-auto">
      {links.map((l) => {
        const Icon = l.icon;
        return (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            data-testid={l.testId}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-3 md:py-2.5 rounded-sm text-base md:text-sm font-medium transition-colors ${
                isActive
                  ? "bg-klein text-white"
                  : "text-slate-300 hover:bg-slate-900 hover:text-white active:bg-slate-800"
              }`
            }
          >
            <Icon size={18} />
            <span>{l.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );

  const Brand = () => (
    <div className="px-6 py-6 md:py-8 border-b border-slate-800">
      <div className="flex items-center gap-3">
        <img
          src="/logo.svg"
          alt="Logo GCA"
          className="w-11 h-11 rounded-sm flex-shrink-0"
          data-testid="sidebar-logo"
        />
        <div className="min-w-0">
          <div className="font-display font-black text-base tracking-tighter text-white leading-tight">
            GESTION
          </div>
          <div className="font-display font-black text-base tracking-tighter text-white leading-tight">
            CONCESSION
          </div>
          <div className="font-display font-black text-base tracking-tighter text-klein leading-tight">
            AUTOMOBILE
          </div>
        </div>
      </div>
    </div>
  );

  const Footer = () => (
    <div className="px-4 py-4 border-t border-slate-800 space-y-2">
      <div className="text-xs text-slate-400 truncate" data-testid="sidebar-user-email">
        {user?.email}
      </div>
      <button
        onClick={handleLogout}
        data-testid="logout-button"
        className="w-full flex items-center justify-center gap-2 px-3 py-3 md:py-2 rounded-sm text-sm bg-slate-900 hover:bg-slate-800 text-slate-200 transition-colors"
      >
        <LogOut size={14} /> Déconnexion
      </button>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 flex-col bg-[#0a0a0a] text-slate-100">
        <Brand />
        <NavItems />
        <Footer />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-[#0a0a0a] text-slate-100 px-3 py-3 flex items-center justify-between shadow-md">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="mobile-menu-trigger"
              aria-label="Ouvrir le menu"
              className="p-2 -ml-1 rounded-sm hover:bg-slate-800 active:bg-slate-700 transition-colors"
            >
              <Menu size={22} />
            </button>
          </SheetTrigger>
          <SheetContent
            side="left"
            className="bg-[#0a0a0a] text-slate-100 border-r border-slate-800 w-[82vw] max-w-[320px] p-0 flex flex-col"
            data-testid="mobile-menu-drawer"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Menu de navigation</SheetTitle>
            </SheetHeader>
            <Brand />
            <NavItems onNavigate={() => setOpen(false)} />
            <Footer />
          </SheetContent>
        </Sheet>

        <div
          className="font-display font-bold tracking-tight text-base truncate px-2"
          data-testid="mobile-page-title"
        >
          <CurrentPageTitle links={links} />
        </div>

        <div className="flex items-center gap-1">
          <Link
            to="/ventes"
            data-testid="mobile-quick-ventes"
            aria-label="Aller aux ventes"
            className="p-2 rounded-sm hover:bg-slate-800 active:bg-slate-700 transition-colors text-amber-400"
            title="Ventes rapides"
          >
            <Car size={20} />
          </Link>
        </div>
      </div>

      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-5 lg:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
